/**
 * Notifications tab — per-user alert config.
 *
 * Three sections:
 *   1. Channels: add/remove email + SMS contact methods.
 *   2. Alerts: pick an alert type, a channel, and a schedule. Two alert types:
 *        - Refill: a recurring summary of items to refill across your boxes
 *          (daily or weekly).
 *        - Manual checkout: a proof-of-delivery email when a manual checkout
 *          happens on a chosen SmartBox — immediately, or as a daily / weekly /
 *          monthly digest. Requires picking a specific SmartBox.
 *      Each alert has its own enabled toggle so you can pause without deleting.
 *   3. Recent activity: last 20 send attempts with status + error.
 *
 * All scoped to the signed-in user server-side (uid from the token). The
 * SmartBox picker is populated from the user's scoped boxes (GET /api/boxes via
 * useBoxes), and the server re-checks SCU access on create.
 */
import { TrashIcon } from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { useBoxes } from "../../hooks/useBoxes";
import { fmtAgo, fmtUtc } from "../../lib/format";
import {
  DOW_BITS,
  createChannel,
  createPreference,
  deleteChannel,
  deletePreference,
  dowMaskLabel,
  fetchChannels,
  fetchNotificationLog,
  fetchPreferences,
  timeLabel,
  updatePreference,
  type AlertType,
  type Cadence,
  type ChannelKind,
  type NotificationChannel,
  type NotificationLogEntry,
  type NotificationPreference,
  NotificationsApiError,
} from "../../services/notificationsApi";

/** Per-smartbox alert types require + scope-check a chosen scu (like the
 *  server's _PER_SCU_ALERT_TYPES). Refill spans all visible boxes instead. */
function isPerScu(t: string): boolean {
  return t === "manual_checkout" || t === "outage";
}

/** "Manual checkout" / "Refill" / "Box down" from the raw alert_type. */
function alertTypeLabel(t: string): string {
  if (t === "manual_checkout") return "Manual checkout";
  if (t === "refill") return "Refill";
  if (t === "outage") return "Box down / outage";
  return t;
}

/** Human schedule summary for a saved preference. */
function scheduleLabel(p: NotificationPreference): string {
  if (p.cadence === "immediate") return "Immediately (per checkout)";
  if (p.cadence === "monthly") {
    return `Monthly on day ${p.day_of_month} at ${timeLabel(p.hour_local, p.minute_local)}`;
  }
  // daily (dow_mask 127 -> "Every day") and weekly
  return `${dowMaskLabel(p.dow_mask)} at ${timeLabel(p.hour_local, p.minute_local)}`;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

export const NotificationsTab: React.FC = () => {
  const { token } = useAuth();
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [logEntries, setLog] = useState<NotificationLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [c, p, l] = await Promise.all([
        fetchChannels(token),
        fetchPreferences(token),
        fetchNotificationLog(token, 20),
      ]);
      setChannels(c);
      setPrefs(p);
      setLog(l);
    } catch (e) {
      setError(e instanceof Error ? e.message : "unknown error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading && channels.length === 0 && prefs.length === 0) {
    return <div className="text-sm text-secondary-500">loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {error && (
        <div className="text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      <ChannelsSection
        token={token}
        channels={channels}
        onChange={refresh}
        onError={setError}
      />

      <AlertsSection
        token={token}
        channels={channels}
        prefs={prefs}
        onChange={refresh}
        onError={setError}
      />

      <ActivitySection entries={logEntries} />
    </div>
  );
};

export default NotificationsTab;

// ----- Channels section ---------------------------------------------------

const ChannelsSection: React.FC<{
  token: string | null;
  channels: NotificationChannel[];
  onChange: () => void;
  onError: (s: string | null) => void;
}> = ({ token, channels, onChange, onError }) => {
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<ChannelKind>("email");
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setKind("email");
    setAddress("");
    setLabel("");
    setAdding(false);
  };

  const onAdd = async () => {
    if (!token) return;
    onError(null);
    setBusy(true);
    try {
      await createChannel(token, {
        kind,
        address: address.trim(),
        label: label.trim() || null,
      });
      reset();
      onChange();
    } catch (e) {
      onError(
        e instanceof NotificationsApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "create failed",
      );
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (cid: number) => {
    if (!token) return;
    if (!window.confirm("Remove this channel? Any alerts using it will be paused.")) {
      return;
    }
    onError(null);
    try {
      await deleteChannel(token, cid);
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : "delete failed");
    }
  };

  return (
    <section className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
          Channels
        </h2>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            Add channel
          </Button>
        )}
      </div>

      {channels.length === 0 && !adding && (
        <p className="text-sm text-secondary-500">
          No channels yet. Add an email or phone number to receive alerts.
        </p>
      )}

      {channels.length > 0 && (
        <ul className="divide-y divide-secondary-100">
          {channels.map((c) => (
            <li
              key={c.channel_id}
              className="py-2 flex items-center gap-3 text-sm"
            >
              <span className="text-xs uppercase font-semibold text-secondary-500 w-10">
                {c.kind}
              </span>
              <span className="font-mono">{c.address}</span>
              {c.label && (
                <span className="text-xs text-secondary-500">({c.label})</span>
              )}
              <button
                type="button"
                onClick={() => void onDelete(c.channel_id)}
                aria-label={`Remove ${c.address}`}
                className="ml-auto text-secondary-500 hover:text-error-700"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="mt-3 border-t border-secondary-100 pt-3 space-y-2 text-sm">
          <div className="flex gap-2 items-center">
            <label className="text-secondary-600">Kind</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ChannelKind)}
              className="border border-secondary-300 rounded px-2 py-1"
            >
              <option value="email">Email</option>
              <option value="sms">SMS (phone)</option>
            </select>
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-secondary-600 w-16">Address</label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={
                kind === "email" ? "you@example.com" : "+15551234567"
              }
              className="flex-1 border border-secondary-300 rounded px-2 py-1 font-mono"
            />
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-secondary-600 w-16">Label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="optional (e.g. work phone)"
              className="flex-1 border border-secondary-300 rounded px-2 py-1"
            />
          </div>
          <p className="text-xs text-secondary-500">
            SMS numbers must be in E.164 format (start with `+` and country code).
          </p>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={reset} disabled={busy}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void onAdd()}
              disabled={busy || !address.trim()}
            >
              {busy ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

// ----- Alerts section -----------------------------------------------------

const AlertsSection: React.FC<{
  token: string | null;
  channels: NotificationChannel[];
  prefs: NotificationPreference[];
  onChange: () => void;
  onError: (s: string | null) => void;
}> = ({ token, channels, prefs, onChange, onError }) => {
  const [adding, setAdding] = useState(false);
  const [alertType, setAlertType] = useState<AlertType>("refill");
  const [scu, setScu] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<number | null>(
    channels[0]?.channel_id ?? null,
  );
  const [cadence, setCadence] = useState<Cadence>("daily");
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [dowMask, setDowMask] = useState(127);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [busy, setBusy] = useState(false);

  // Scoped boxes for the manual-checkout SmartBox picker (same scoping the
  // rest of the app uses; the server re-checks access on create).
  const { snapshot: boxesSnapshot } = useBoxes();
  const boxOptions = useMemo(() => {
    const boxes = boxesSnapshot?.boxes ?? [];
    return boxes
      .map((b) => ({
        scu: b.scu_name,
        label:
          [b.smartbox_code, b.customer].filter(Boolean).join(" · ") ||
          b.scu_name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [boxesSnapshot]);

  // If channels change while the form is open, keep the picker valid.
  useEffect(() => {
    if (channelId !== null && !channels.find((c) => c.channel_id === channelId)) {
      setChannelId(channels[0]?.channel_id ?? null);
    }
  }, [channels, channelId]);

  const reset = () => {
    setAdding(false);
    setAlertType("refill");
    setScu(null);
    setCadence("daily");
    setHour(9);
    setMinute(0);
    setDowMask(127);
    setDayOfMonth(1);
  };

  // Switching alert type resets scope + coerces the cadence to one the chosen
  // type allows (refill: daily/weekly; manual checkout: also immediate/monthly).
  const onChangeAlertType = (t: AlertType) => {
    setAlertType(t);
    if (t === "refill") {
      setScu(null);
      if (cadence !== "daily" && cadence !== "weekly") setCadence("daily");
    } else {
      setCadence("immediate");
    }
  };

  const onAdd = async () => {
    if (!token || channelId === null) return;
    if (isPerScu(alertType) && !scu) {
      onError("Pick a SmartBox for this subscription.");
      return;
    }
    onError(null);
    setBusy(true);
    try {
      await createPreference(token, {
        alert_type: alertType,
        channel_id: channelId,
        cadence,
        // hour/minute are ignored server-side for 'immediate'.
        ...(cadence !== "immediate"
          ? { hour_local: hour, minute_local: minute }
          : {}),
        dow_mask: cadence === "weekly" ? dowMask : 127,
        ...(cadence === "monthly" ? { day_of_month: dayOfMonth } : {}),
        ...(isPerScu(alertType) ? { scu } : {}),
      });
      reset();
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  };

  const onToggle = async (p: NotificationPreference, enabled: boolean) => {
    if (!token) return;
    onError(null);
    try {
      await updatePreference(token, p.preference_id, { enabled });
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : "update failed");
    }
  };

  const onDelete = async (pid: number) => {
    if (!token) return;
    if (!window.confirm("Remove this alert?")) return;
    onError(null);
    try {
      await deletePreference(token, pid);
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : "delete failed");
    }
  };

  const toggleDow = (bit: number) => {
    setDowMask((prev) => (prev & bit ? prev & ~bit : prev | bit));
  };

  const noChannels = channels.length === 0;

  return (
    <section className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
          Alerts
        </h2>
        {!adding && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            disabled={noChannels}
            title={noChannels ? "Add a channel first" : undefined}
          >
            Add alert
          </Button>
        )}
      </div>

      {noChannels && (
        <p className="text-sm text-secondary-500">
          Add a channel first; alerts need somewhere to send to.
        </p>
      )}

      {!noChannels && prefs.length === 0 && !adding && (
        <p className="text-sm text-secondary-500">
          No alerts configured. Click <em>Add alert</em> to create one.
        </p>
      )}

      {prefs.length > 0 && (
        <ul className="divide-y divide-secondary-100">
          {prefs.map((p) => (
            <li key={p.preference_id} className="py-2 text-sm">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold">{alertTypeLabel(p.alert_type)}</span>
                {p.scu && (
                  <span className="text-xs font-mono text-secondary-600 bg-secondary-50 border border-secondary-200 rounded px-1.5 py-0.5">
                    {p.scu}
                  </span>
                )}
                <span className="text-secondary-500">→</span>
                <span className="text-xs uppercase font-semibold text-secondary-500">
                  {p.channel_kind}
                </span>
                <span className="font-mono">{p.channel_address}</span>
                <label className="ml-auto flex items-center gap-2 text-xs text-secondary-700">
                  <input
                    type="checkbox"
                    checked={p.enabled}
                    onChange={(e) => void onToggle(p, e.target.checked)}
                  />
                  {p.enabled ? "on" : "off"}
                </label>
                <button
                  type="button"
                  onClick={() => void onDelete(p.preference_id)}
                  aria-label="Remove alert"
                  className="text-secondary-500 hover:text-error-700"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="text-xs text-secondary-500 mt-1">
                {scheduleLabel(p)}
                {p.cadence !== "immediate" ? " (your company time)" : ""}
                {p.last_fired_utc && (
                  <span className="ml-2">
                    · last sent {fmtAgo(p.last_fired_utc)} ({p.last_fire_status})
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding && channelId !== null && (
        <div className="mt-3 border-t border-secondary-100 pt-3 space-y-2 text-sm">
          <div className="flex gap-2 items-center flex-wrap">
            <label className="text-secondary-600">Alert type</label>
            <select
              value={alertType}
              onChange={(e) => onChangeAlertType(e.target.value as AlertType)}
              className="border border-secondary-300 rounded px-2 py-1"
            >
              <option value="refill">Refill reminders</option>
              <option value="manual_checkout">Manual checkout reports</option>
              <option value="outage">Box down / outage alerts</option>
            </select>
          </div>
          <p className="text-xs text-secondary-500">
            {alertType === "refill"
              ? "A recurring summary of items that need refilling across your boxes."
              : alertType === "outage"
                ? "An alert when the chosen SmartBox is DOWN and stays down (fires at the configured milestones — e.g. 1h and 24h). Pick Immediately for milestone alerts."
                : "An email when a manual checkout happens on the chosen SmartBox — the signed proof of delivery, the items, and the form details. Choose Immediately for one email per checkout, or roll them into a digest."}
          </p>

          {isPerScu(alertType) && (
            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-secondary-600">SmartBox</label>
              <select
                value={scu ?? ""}
                onChange={(e) => setScu(e.target.value || null)}
                className="border border-secondary-300 rounded px-2 py-1 max-w-full"
              >
                <option value="" disabled>
                  Select a SmartBox…
                </option>
                {boxOptions.map((b) => (
                  <option key={b.scu} value={b.scu}>
                    {b.label}
                  </option>
                ))}
              </select>
              {boxOptions.length === 0 && (
                <span className="text-xs text-secondary-500">
                  (loading your SmartBoxes…)
                </span>
              )}
            </div>
          )}

          <div className="flex gap-2 items-center flex-wrap">
            <label className="text-secondary-600">Send to</label>
            <select
              value={channelId}
              onChange={(e) => setChannelId(Number(e.target.value))}
              className="border border-secondary-300 rounded px-2 py-1"
            >
              {channels.map((c) => (
                <option key={c.channel_id} value={c.channel_id}>
                  {c.kind.toUpperCase()} {c.address}
                  {c.label ? ` (${c.label})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <label className="text-secondary-600">When</label>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value as Cadence)}
              className="border border-secondary-300 rounded px-2 py-1"
            >
              {isPerScu(alertType) && (
                <option value="immediate">
                  {alertType === "outage" ? "Immediately (per milestone)" : "Immediately (per checkout)"}
                </option>
              )}
              <option value="daily">Every day</option>
              <option value="weekly">Specific days</option>
              {isPerScu(alertType) && (
                <option value="monthly">Monthly</option>
              )}
            </select>
            {cadence !== "immediate" && (
              <>
                <span className="text-secondary-600">at</span>
                <select
                  value={hour}
                  onChange={(e) => setHour(Number(e.target.value))}
                  className="border border-secondary-300 rounded px-2 py-1"
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={minute}
                  onChange={(e) => setMinute(Number(e.target.value))}
                  className="border border-secondary-300 rounded px-2 py-1"
                >
                  {MINUTES.map((m) => (
                    <option key={m} value={m}>
                      {String(m).padStart(2, "0")}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
          {cadence === "monthly" && (
            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-secondary-600">Day of month</label>
              <select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
                className="border border-secondary-300 rounded px-2 py-1"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <span className="text-xs text-secondary-500">
                (1–28, so every month has the day)
              </span>
            </div>
          )}
          {cadence === "weekly" && (
            <div className="flex gap-1 items-center flex-wrap">
              <label className="text-secondary-600 mr-2">Days</label>
              {DOW_BITS.map((d) => (
                <button
                  key={d.bit}
                  type="button"
                  onClick={() => toggleDow(d.bit)}
                  className={
                    "px-2 py-1 rounded text-xs border " +
                    (dowMask & d.bit
                      ? "bg-primary-100 text-primary-800 border-primary-300"
                      : "bg-white text-secondary-600 border-secondary-300 hover:bg-secondary-50")
                  }
                >
                  {d.label}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-secondary-500">
            {cadence === "immediate"
              ? "Sends as soon as a checkout is recorded (after a short wait for the signature to upload)."
              : "Time is in your company timezone. The alert fires once per scheduled slot — if the runner is briefly offline it catches up on the next cycle."}
          </p>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={reset} disabled={busy}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void onAdd()}
              disabled={
                busy ||
                (cadence === "weekly" && dowMask === 0) ||
                (alertType === "manual_checkout" && !scu)
              }
            >
              {busy ? "Adding…" : "Add alert"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

// ----- Activity section ---------------------------------------------------

const ActivitySection: React.FC<{ entries: NotificationLogEntry[] }> = ({
  entries,
}) => (
  <section className="bg-white border border-secondary-200 rounded-md p-4">
    <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700 mb-3">
      Recent activity
    </h2>
    {entries.length === 0 ? (
      <p className="text-sm text-secondary-500">No alerts sent yet.</p>
    ) : (
      <ul className="divide-y divide-secondary-100">
        {entries.map((e) => (
          <li key={e.log_id} className="py-2 text-sm">
            <div className="flex items-center gap-2">
              <span
                className={
                  "text-xs uppercase font-semibold px-1.5 py-0.5 rounded " +
                  (e.status === "sent"
                    ? "bg-success-50 text-success-700 border border-success-200"
                    : "bg-error-50 text-error-700 border border-error-200")
                }
              >
                {e.status}
              </span>
              <span className="text-xs uppercase text-secondary-500">
                {e.channel_kind}
              </span>
              <span className="font-mono text-xs">{e.address}</span>
              <span className="text-xs text-secondary-500 ml-auto">
                {e.sent_utc ? fmtUtc(e.sent_utc) : "—"}
              </span>
            </div>
            {e.subject && (
              <div className="text-secondary-800 mt-1">{e.subject}</div>
            )}
            {e.error && (
              <div className="text-xs text-error-700 mt-1 whitespace-pre-wrap">
                {e.error}
              </div>
            )}
            {e.body_preview && !e.error && (
              <div className="text-xs text-secondary-500 mt-1 whitespace-pre-wrap">
                {e.body_preview.slice(0, 200)}
                {e.body_preview.length > 200 ? "…" : ""}
              </div>
            )}
          </li>
        ))}
      </ul>
    )}
  </section>
);
